import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import gstVerificationCounterService from '../../services/GstVerificationCounterService.js';
import { ShieldCheck, CheckCircle, AlertTriangle } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import Loader from '../common/Loader.js';
import { useTranslation } from 'react-i18next';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';


const GstVerificationCounterList = () => {
  const { t } = useTranslation();
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 10;
  const [openMenuId, setOpenMenuId] = useState(null);
  const [emailingId, setEmailingId] = useState(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const fetchCounters = useCallback(async () => {
    try {
      setLoading(true);
      const response = await gstVerificationCounterService.getAll(currentPage, perPage, {
        search: filters.search || undefined,
        status: filters.status || undefined,
      });
      setCounters((prev) => mergeLoadedPage(currentPage, response.data || [], prev));
      setTotalPages(response.pagination?.pages || 1);
    } catch (err) {
      console.error(err);
      Alert.error('Failed to load GST verification counters');
      if (currentPage <= 1) setCounters([]);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage]);

  usePaginatedListFetch({
    fetchPage: fetchCounters,
    setCurrentPage,
    currentPage,
    resetPageDeps: [filters.search, filters.status],
    debounceWhen: filters.search,
    debounceMs: 0,
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clearFilters = () => {
    setFilters({ search: '', status: '' });
  };

  const hasActiveFilters = filters.search || filters.status;

  const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', {
      style: 'currency', currency: 'INR',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });

  const getCompanyName = (c) => c.company_name || c.company?.company_name || 'N/A';

  const handleDownloadInvoice = async (e, companyId, companyName) => {
    e.stopPropagation();
    setOpenMenuId(null);
    try {
      const blob = await gstVerificationCounterService.downloadInvoice(companyId, true);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `gst_verification_invoice_${companyName}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('Invoice downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download invoice');
    }
  };

  const handleEmailInvoice = async (e, counter) => {
    e.stopPropagation();
    setOpenMenuId(null);
    const companyId = counter.company_id;
    const companyEmail = counter.company_email || counter.company?.email || counter.email;
    const companyName = getCompanyName(counter);
    const confirmed = await Alert.confirm(
      companyEmail
        ? `Send GST verification invoice for ${companyName} to ${companyEmail}?`
        : `Send GST verification invoice for ${companyName} to the company email?`,
      'Email Invoice'
    );
    if (!confirmed) return;
    try {
      setEmailingId(companyId);
      const res = await gstVerificationCounterService.sendInvoiceEmail(companyId, true);
      Alert.success(res.message || 'Invoice emailed successfully!');
    } catch (err) {
      Alert.error(err.response?.data?.error || err.response?.data?.message || 'Failed to email invoice');
    } finally {
      setEmailingId(null);
    }
  };

  // Summary counts
  const totalDue = counters.reduce((s, c) => s + Math.max(0, parseFloat(c.due_amount || 0)), 0);
  const withDue = counters.filter(c => (c.due_amount || 0) > 0).length;
  const paidUp = counters.filter(c => (c.due_amount || 0) <= 0).length;

  return (
    <DashboardLayout>
      {loading && currentPage === 1 ? (
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading GST verification counters..." />
        </div>
      ) : (
        <>
              <ListPageHeader
                title="GST Verification Counter"
                subtitle="Track GST verification usage and billing per company"
                icon={ShieldCheck}
                titleClassName="text-gray-900"
                className="mb-6"
              />

              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
                <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 flex items-center gap-2 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs text-gray-500 font-medium truncate">Total Companies</p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900">{counters.length}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 flex items-center gap-2 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs text-gray-500 font-medium truncate">Pending Due</p>
                    <p className="text-lg sm:text-xl font-bold text-red-600">{withDue} <span className="text-xs sm:text-sm font-normal text-gray-500">companies</span></p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 flex items-center gap-2 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs text-gray-500 font-medium truncate">Paid Up</p>
                    <p className="text-lg sm:text-xl font-bold text-green-600">{paidUp} <span className="text-xs sm:text-sm font-normal text-gray-500">companies</span></p>
                  </div>
                </div>
              </div>

              <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
                <div>
                  <SearchSubmitInput
                    value={filters.search}
                    onSearch={(value) => setFilters({ ...filters, search: value })}
                    placeholder="Search by company name or ID..."
                    inputClassName="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <select
                    value={filters.status}
                    onChange={e => setFilters({ ...filters, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="due">With Due Amount</option>
                    <option value="noDue">Paid Up</option>
                  </select>
                </div>
              </ListFilterPanel>

              {/* Table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {counters.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ShieldCheck className="w-8 h-8 text-gray-300" />
                    </div>
                    <p className="text-gray-500 font-medium">No GST verification counters found</p>
                    <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filter</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile card view */}
                    <div className="md:hidden p-3 space-y-3">
                      {counters.map((counter) => {
                        const companyName = getCompanyName(counter);
                        const due = counter.due_amount || 0;
                        const isPaid = due <= 0;

                        return (
                          <div
                            key={counter.id}
                            className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-2 mb-3">
                              <button
                                type="button"
                                onClick={() => navigate(`/company-gst-verification-counters/view/${counter.company_id}`)}
                                className="text-left min-w-0 flex-1"
                              >
                                <p className="text-sm font-semibold text-blue-600 truncate">{companyName}</p>
                                {counter.last_search_at && (
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    Last: {new Date(counter.last_search_at).toLocaleDateString('en-IN')}
                                  </p>
                                )}
                              </button>
                              <div className="flex items-center gap-2 shrink-0">
                                {isPaid ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                    <CheckCircle className="w-3 h-3" /> Paid
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                                    <AlertTriangle className="w-3 h-3" /> Due
                                  </span>
                                )}
                                <RowActionsMenu
                                  isOpen={openMenuId === counter.id}
                                  onToggle={() => setOpenMenuId(openMenuId === counter.id ? null : counter.id)}
                                  onClose={() => setOpenMenuId(null)}
                                  menuRef={menuRef}
                                  onView={() => navigate(`/company-gst-verification-counters/view/${counter.company_id}`)}
                                  extraItems={[
                                    {
                                      label: 'Download Invoice',
                                      icon: '📥',
                                      onClick: () => handleDownloadInvoice({ stopPropagation: () => {} }, counter.company_id, companyName),
                                    },
                                    {
                                      label: emailingId === counter.company_id ? 'Emailing...' : 'Email Invoice',
                                      icon: '✉️',
                                      onClick: () => handleEmailInvoice({ stopPropagation: () => {} }, counter),
                                    },
                                  ]}
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-gray-500">Searches:</span>
                                <p className="text-gray-900 font-semibold mt-0.5">
                                  {(counter.search_count || 0).toLocaleString()}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">Cost/Search:</span>
                                <p className="text-gray-900 font-medium mt-0.5">
                                  {formatCurrency(counter.cost_per_search || 0)}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">Total Cost:</span>
                                <p className="text-gray-900 font-semibold mt-0.5">
                                  {formatCurrency(counter.total_cost || 0)}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">Paid:</span>
                                <p className="text-green-600 font-semibold mt-0.5">
                                  {formatCurrency(counter.paid_amount || 0)}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Tablet / desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-12">Sr</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('common.company')}</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Searches</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Cost/Search</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Cost</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Paid</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide w-16">{t('common.actions')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {counters.map((counter, idx) => {
                          const companyName = getCompanyName(counter);
                          const due = counter.due_amount || 0;
                          const isPaid = due <= 0;

                          return (
                            <tr key={counter.id} className="hover:bg-gray-50 transition-colors">
                              {/* Sr No */}
                              <td className="px-4 py-3.5 text-sm text-gray-400 font-medium">{idx + 1}</td>

                              {/* Company Name - clickable */}
                              <td className="px-4 py-3.5">
                                <button
                                  onClick={() => navigate(`/company-gst-verification-counters/view/${counter.company_id}`)}
                                  className="text-left group"
                                >
                                  <p className="text-sm font-semibold text-blue-600 group-hover:text-blue-700 group-hover:underline">
                                    {companyName}
                                  </p>
                                  {counter.last_search_at && (
                                    <p className="text-xs text-gray-400 mt-0.5">
                                      Last: {new Date(counter.last_search_at).toLocaleDateString('en-IN')}
                                    </p>
                                  )}
                                </button>
                              </td>

                              {/* Searches */}
                              <td className="px-4 py-3.5 text-center">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">
                                  <ShieldCheck className="w-3 h-3" />
                                  {(counter.search_count || 0).toLocaleString()}
                                </span>
                              </td>

                              {/* Cost/Search */}
                              <td className="px-4 py-3.5 text-right text-sm text-gray-600">
                                {formatCurrency(counter.cost_per_search || 0)}
                              </td>

                              {/* Total Cost */}
                              <td className="px-4 py-3.5 text-right text-sm font-semibold text-gray-900">
                                {formatCurrency(counter.total_cost || 0)}
                              </td>

                              {/* Paid */}
                              <td className="px-4 py-3.5 text-right text-sm font-semibold text-green-600">
                                {formatCurrency(counter.paid_amount || 0)}
                              </td>

                              {/* Status */}
                              <td className="px-4 py-3.5 text-center">
                                {isPaid ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                    <CheckCircle className="w-3 h-3" /> Paid Up
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                                    <AlertTriangle className="w-3 h-3" /> {formatCurrency(due)}
                                  </span>
                                )}
                              </td>

                              {/* 3-dot Actions */}
                              <td className="px-4 py-3.5 text-center">
                                <RowActionsMenu
                                  isOpen={openMenuId === counter.id}
                                  onToggle={() => setOpenMenuId(openMenuId === counter.id ? null : counter.id)}
                                  onClose={() => setOpenMenuId(null)}
                                  menuRef={menuRef}
                                  onView={() => navigate(`/company-gst-verification-counters/view/${counter.company_id}`)}
                                  extraItems={[
                                    {
                                      label: 'Download Invoice',
                                      icon: '📥',
                                      onClick: () => handleDownloadInvoice({ stopPropagation: () => {} }, counter.company_id, companyName),
                                    },
                                    {
                                      label: emailingId === counter.company_id ? 'Emailing...' : 'Email Invoice',
                                      icon: '✉️',
                                      onClick: () => handleEmailInvoice({ stopPropagation: () => {} }, counter),
                                    },
                                  ]}
                                  className="relative inline-block"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    </div>

                    <ListPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      loading={loading}
                      onNext={() => setCurrentPage((page) => page + 1)}
                    />

                    {withDue > 0 && (
                      <div className="md:hidden px-4 py-3 bg-gray-50 border-t border-gray-200">
                        <p className="text-xs text-red-600 font-medium text-center">
                          Total Outstanding: {formatCurrency(totalDue)}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

    </>
      )}
    </DashboardLayout>
  );
};

export default GstVerificationCounterList;